import { useEffect } from 'react';
import { Card, Icon, Link, Text } from '@nimbus-ds/components';
import { LockIcon, PlusCircleIcon } from '@nimbus-ds/icons';
import { AddressingConfigurationRuleResponseDto } from '@tiendanube/common';
import { FEATURE_MAX_LOCATIONS } from 'App/features';
import { useNavegate } from 'App/hooks';
import { ErrorScreen, Stack } from 'commons/components';
import { SHIPPING_ADD_LOCATION } from 'config/upsellFlowSources';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import useMultiCDActivation from 'domains/Shipping/Locations/hooks/useMultiCDActivation';
import { trackingShippinCreateSecondaryCDByAddressingRuleCard } from 'domains/Shipping/tracking';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import { ADDRESSING_RULES_TRANSLATE_TYPES } from '../constants';

interface RulesListBodyAndFooterProps {
  rules: AddressingConfigurationRuleResponseDto[];
  isError: boolean;
  fetchRules: () => void;
  openMulticdModal: () => void;
}

function RulesListBodyAndFooter({
  rules,
  isError,
  fetchRules,
  openMulticdModal,
}: Readonly<RulesListBodyAndFooterProps>) {
  const { Body, Footer } = Card;
  const t = useTranslationShipping('deliveryMethods.addressingConfigurationV2');
  const { goTo } = useNavegate();
  const { available, getMultiCDAvailability } = useMultiCDActivation();

  const handleClickAdd = useUpsellFlow({
    title: t('info.locations.upsell'),
    featureKey: FEATURE_MAX_LOCATIONS,
    minValue: 2,
    trackingSource: SHIPPING_ADD_LOCATION,
    callback: () => {
      if (available) {
        trackingShippinCreateSecondaryCDByAddressingRuleCard('locations');
        goTo('/settings/locations/new/secondary');
      }

      if (!available) {
        trackingShippinCreateSecondaryCDByAddressingRuleCard(
          'multicd-unavailable',
        );
        openMulticdModal();
      }
    },
    onCloseModal: () => {
      trackingShippinCreateSecondaryCDByAddressingRuleCard('plans');
    },
  });

  useEffect(() => {
    getMultiCDAvailability();
  }, [getMultiCDAvailability]);

  return (
    <>
      <Body>
        {!isError && (
          <Stack column spacing="base" align="stretch">
            <Text fontSize="base" lineHeight="base" textAlign="left">
              {t('info.disabledDescription')}
            </Text>
            {rules.map((rule) => (
              <Stack key={rule.code} spacing="tight">
                <Icon color="neutral-textLow" source={<LockIcon />} />
                <Text color="neutral-textLow">
                  {rule.type === 'APP'
                    ? rule.appName
                    : t(`types.${ADDRESSING_RULES_TRANSLATE_TYPES[rule.type]}`)}
                </Text>
              </Stack>
            ))}
          </Stack>
        )}
        {isError && (
          <ErrorScreen
            description={t('info.error.description')}
            onRetry={fetchRules}
          />
        )}
      </Body>
      {!isError && (
        <Footer>
          <Link
            as="a"
            onClick={handleClickAdd}
            appearance="primary"
            textDecoration="none"
          >
            <PlusCircleIcon />
            {t('info.locations.title')}
          </Link>
        </Footer>
      )}
    </>
  );
}

export default RulesListBodyAndFooter;
